import React from 'react';
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Box,
  Divider,
  Typography,
  useTheme,
  useMediaQuery,
  Toolbar
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Store as StoreIcon,
  People as PeopleIcon,
  Assessment as AssessmentIcon,
  Settings as SettingsIcon,
  Security as SecurityIcon,
  Backup as BackupIcon,
  BugReport as BugReportIcon,
  Groups as GroupsIcon,
  Email as EmailIcon
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAdminAuth } from '../context/AdminAuthContext';
import type { AdminAuthContextType } from '../context/AdminAuthContext';

interface AdminNavigationProps {
  open: boolean;
  onClose?: () => void;
  variant?: 'permanent' | 'persistent' | 'temporary';
}

interface NavigationItem {
  id: string;
  label: string;
  icon: React.ReactElement;
  path: string;
  divider?: boolean;
}

const AdminNavigation: React.FC<AdminNavigationProps> = ({
  open,
  onClose,
  variant = 'permanent'
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const location = useLocation();
  const adminAuth = useAdminAuth() as AdminAuthContextType;
  const { admin } = adminAuth;

  const drawerWidth = 240;

  // システム管理者用ナビゲーションアイテム
  const navigationItems: NavigationItem[] = [
    {
      id: 'dashboard',
      label: 'ダッシュボード',
      icon: <DashboardIcon />,
      path: '/admin/dashboard'
    },
    {
      id: 'stores',
      label: '店舗管理',
      icon: <StoreIcon />,
      path: '/admin/stores'
    },
    {
      id: 'users',
      label: 'ユーザー管理',
      icon: <PeopleIcon />,
      path: '/admin/users'
    },
    {
      id: 'staff',
      label: 'スタッフ管理',
      icon: <GroupsIcon />,
      path: '/admin/staff'
    },
    {
      id: 'reports',
      label: 'レポート',
      icon: <AssessmentIcon />,
      path: '/admin/reports'
    },
    {
      id: 'mail-test',
      label: 'メールテスト',
      icon: <EmailIcon />,
      path: '/admin/mail-test',
      divider: true
    },
    {
      id: 'system-settings',
      label: 'システム設定',
      icon: <SettingsIcon />,
      path: '/admin/system-settings'
    },
    {
      id: 'security',
      label: 'セキュリティ',
      icon: <SecurityIcon />,
      path: '/admin/security'
    },
    {
      id: 'backup',
      label: 'バックアップ',
      icon: <BackupIcon />,
      path: '/admin/backup'
    },
    {
      id: 'logs',
      label: 'システムログ',
      icon: <BugReportIcon />,
      path: '/admin/logs'
    }
  ];

  const handleNavigate = (path: string) => {
    navigate(path);
    if (isMobile && onClose) {
      onClose();
    }
  };

  const drawerContent = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Toolbarでヘッダーの高さ分のスペースを確保 */}
      <Toolbar />
      {/* ヘッダー部分 */}
      <Box sx={{ p: 2, pb: 1.5, borderBottom: 1, borderColor: 'divider' }}>
        <Typography variant="h6" component="div" sx={{ fontWeight: 'bold', mb: 0.5 }}>
          システム管理
        </Typography>
        {admin && (
          <Typography variant="caption" color="text.secondary">
            {admin.username}
          </Typography>
        )}
      </Box>

      {/* ナビゲーションリスト */}
      <List sx={{ flexGrow: 1, p: 1 }}>
        {navigationItems.map((item, index) => (
          <React.Fragment key={item.id}>
            <ListItem disablePadding sx={{ mb: 0.5 }}>
              <ListItemButton
                onClick={() => handleNavigate(item.path)}
                selected={location.pathname === item.path}
                sx={{
                  borderRadius: 1,
                  '&.Mui-selected': {
                    backgroundColor: theme.palette.action.selected,
                    '&:hover': {
                      backgroundColor: theme.palette.action.selected
                    }
                  }
                }}
              >
                <ListItemIcon
                  sx={{
                    color:
                      location.pathname === item.path
                        ? theme.palette.primary.main
                        : theme.palette.text.secondary,
                    minWidth: 40
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                <ListItemText
                  primary={item.label}
                  primaryTypographyProps={{
                    fontSize: '0.875rem',
                    fontWeight: location.pathname === item.path ? 600 : 400
                  }}
                />
              </ListItemButton>
            </ListItem>
            {item.divider && index < navigationItems.length - 1 && <Divider sx={{ my: 1 }} />}
          </React.Fragment>
        ))}
      </List>

      {/* フッター */}
      <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
        <Typography variant="caption" color="text.secondary" align="center" display="block">
          © 2025 ニコニコシステム
        </Typography>
      </Box>
    </Box>
  );

  return (
    <Drawer
      variant={isMobile ? 'temporary' : variant}
      open={open}
      onClose={onClose}
      sx={{
        width: drawerWidth,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: drawerWidth,
          boxSizing: 'border-box',
          // デスクトップ表示時のスタイル
          ...(variant === 'permanent' && {
            zIndex: theme.zIndex.drawer
          }),
          // モバイル表示時のスタイル
          ...(variant === 'temporary' && {
            top: 0,
            height: '100%',
            zIndex: theme.zIndex.drawer + 1 // ヘッダーより上に表示
          })
        }
      }}
      ModalProps={{
        keepMounted: true // モバイルでのパフォーマンス向上
      }}
    >
      {drawerContent}
    </Drawer>
  );
};

export default AdminNavigation;
